import * as React from 'react';
import {useCallback, useEffect, useMemo, useState, useRef} from 'react';

import {Ages, dateToDayOfWeekNum, dateToFormat, dateToServerDateTs, tsToDate, tsToDayOfWeekNum} from '../../../utils/ages'
import {isTsWithinFreeze} from '../../../utils/frozen'
import {holidayForTs, isHolidayTs} from '../../../utils/holidays'
import {AvailableDateOption, SlotsPage} from '../components';
import {useAppSelector} from "../../../redux/hooks";
import {profileByIdSelector, useCustomerStore} from "../../../redux/customers";
import {bookingsByProfilePackageIdSelector, useBookingsStore} from "../../../redux/bookings";
import {profilePackageByIdSelector, useProfilePackageStore} from "../../../redux/profile-packages";
import {slotsByDayOfWeekSelector, useSlotsStore} from "../../../redux/slots";
import {useToastStore} from "../../../redux/toast";
import {Employee, Facility, LessonType, Slot} from "../../../models";
import {useIonAlert} from "@ionic/react";
import {useHolidaysStore} from "../../../redux/holidays";
import {useWaitingBookingsStore} from "../../../redux/waiting-bookings";

type PackageDetailsPageContainerProps = {
    profilePackageId?: number
};

export const SlotsPageContainer: React.FC<PackageDetailsPageContainerProps> = props => {
    // Page Props (Url)
    const {profilePackageId} = props;

    // Date Filters
    const [selectedDate, setSelectedDate] = useState<number>(-1);
    const [selectedDateIsToday, setSelectedDateIsToday] = useState<boolean>(false);
    const [selectedWeekIndex, setSelectedWeekIndex] = useState<number>(-99);

    // Triggers & UI
    // const refresherRef = useRef<HTMLIonRefresherElement>(null);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState<boolean>(false);
    const [isSlotModalOpen, setIsSlotModalOpen] = useState<boolean>(false);
    const [selectedSlot, setSelectedSlot] = useState<Slot>();
    // Recurring self-service "move": set once the user taps Move on a booked
    // occurrence, cleared on a successful move or on cancel. While set, the
    // book flow across the page is repointed at the move destination picker.
    const [movingBooking, setMovingBooking] = useState<{ bookingId: number; description: string } | null>(null);
    const lastProfilePackageId = useRef<number | undefined>(undefined);
    // Direct Selectors
    const profilePackage = useAppSelector((state) => profilePackageByIdSelector(state, profilePackageId));
    const visibleSlots = useAppSelector((state) => slotsByDayOfWeekSelector(state, tsToDayOfWeekNum(selectedDate) ));
    const bookings = useAppSelector((state) => bookingsByProfilePackageIdSelector(state, profilePackageId));
    const profile = useAppSelector((state) => profileByIdSelector(state, profilePackage?.learnerId));
    // A frozen profile can't be booked for a date inside its freeze window. frozenUntil is the
    // (inclusive) end of the freeze active today; the API enforces per-date too — including
    // future-dated freezes this client-only value can't see — so this is the courtesy gate.
    const frozenUntil = profile?.frozenUntil ?? null;
    const isSelectedDateFrozen = isTsWithinFreeze(frozenUntil, selectedDate);
    // Store & Action Selectors
    const toastStore = useToastStore();
    const customerStore = useCustomerStore();
    const {activeCompany: company, activeCustomer} = customerStore;
    const profilePackageStore = useProfilePackageStore();
    const {getById: getProfilePackageById, isFetching: isFetchingPackages} = profilePackageStore;
    const slotsStore = useSlotsStore();
    const {isFetching, slotsBySelectedDrawKey: slots, existingDrawKeys, setSelectedDraw, clearProfilePackageDraws, getAllSlotsWeekly, errorMessage: slotsErrorMessage, clearErrors: slotClearErrors} = slotsStore;
    
    // Loading states
    const isInitialLoading = !isInitialLoadComplete && isFetching;
    const isRefreshing = isInitialLoadComplete && isFetching;

    const selectedWeekStartTs =
        selectedWeekIndex > -99 ? dateToServerDateTs(getSelectedWeekStartDate().date) : undefined;

    const selectedDrawKey =
        profilePackage && selectedWeekStartTs
            ? `${profilePackage.id}-${selectedWeekStartTs}`
            : undefined;

    const hasSelectedDraw =
        !!selectedDrawKey && existingDrawKeys.includes(selectedDrawKey);

    const isChangingWeek =
        isInitialLoadComplete &&
        isFetching &&
        !!selectedDrawKey &&
        !hasSelectedDraw;
    const holidaysStore = useHolidaysStore();
    const {holidaysByActiveCustomer: holidays} = holidaysStore;
    const selectedHoliday = holidayForTs(holidays ?? [], selectedDate);
    const selectedHolidayLabel = !isSelectedDateFrozen && selectedHoliday ? selectedHoliday.name : null;
    const bookingsStore = useBookingsStore();
    const {isChangingBooking, bookingCreate, bookingDelete, bookingCancel, bookingUncancel, bookingMove, errorMessage: bookingsErrorMessage, clearErrors: bookingsClearErrors } = bookingsStore;

    const waitingBookingsStore = useWaitingBookingsStore();
    const {isChangingWaitingBooking, waitingBookingCreate, waitingBookingDelete} = waitingBookingsStore;

    // A waiting join/leave POST must disable the row's controls too: the API's
    // can_wait check is read-then-write, so an un-guarded double-tap can
    // double-insert (F19).
    const actionsDisabled = isChangingBooking || isChangingWaitingBooking || isInitialLoading || isChangingWeek;
    const [presentConfirmAlert] = useIonAlert();

    // Config
    const weeksAvailablePast = profilePackage?.weeksAvailablePast || 1;
    const weeksAvailableFuture = profilePackage?.weeksAvailableFuture || 3;


    // Filters
    const [isSlotFilterModalOpen, setIsSlotFilterModalOpen] = useState<boolean>(false);
    const [filterBookedOnly, setFilterBookedOnly] = useState<boolean>(false);
    const [filterAvailableOnly, setFilterAvailableOnly] = useState<boolean>(false);
    const [filterSlotType, setFilterSlotType] = useState<number>();
    const [filterFacility, setFilterFacility] = useState<number>();
    const [filterEmployee, setFilterEmployee] = useState<number>();

    // Ionic reuses the mounted page instance when only the /slots/:id param
    // changes, so all of this component state survives a profile-package
    // switch. Reset it during render so a switch behaves like a fresh mount:
    // with isInitialLoadComplete false the week effect does a full fetch
    // (slots plus the new learner's bookings and waiting list) instead of
    // short-circuiting onto a persisted draw, and the previous package's
    // filters don't carry over (F20). A reset inside the package-change
    // effect below would be too late — the fetch effect runs in the same
    // effect flush with the pre-reset values and never re-fires.
    const [renderedProfilePackageId, setRenderedProfilePackageId] = useState(profilePackageId);
    if (profilePackageId !== renderedProfilePackageId) {
        setRenderedProfilePackageId(profilePackageId);
        setSelectedWeekIndex(-99);
        setIsInitialLoadComplete(false);
        setFilterBookedOnly(false);
        setFilterAvailableOnly(false);
        setFilterSlotType(undefined);
        setFilterFacility(undefined);
        setFilterEmployee(undefined);
        setMovingBooking(null);
    }

    // Prepare facility filters
    const facilities = useMemo(() => visibleSlots ? visibleSlots.reduce((acc: Facility[], slot) => {
        if(slot.facilityId && slot.facility && !acc.find(item => item.id === slot.facilityId)) {
            acc.push(slot.facility);
        }
        return acc;
    }, []).sort((a, b) => a.name.localeCompare(b.name))  : [], [visibleSlots]);
    // Prepare employee filters
    const employees = useMemo(() => visibleSlots ? visibleSlots.reduce((acc: Employee[], slot) => {
        if(slot.employees) {
            slot.employees.forEach((employee) => {
                if (!acc.find(e => e.id === employee.id)) {
                    acc.push(employee);
                }
            });
        }
        return acc;
    }, []).sort((a, b) => a.fullName.localeCompare(b.fullName))  : [], [visibleSlots]);
    // Prepare slot type filters
    const slotTypes = useMemo(() => visibleSlots ? visibleSlots.reduce((acc: LessonType[], slot) => {
        if(slot.lessonType && !acc.find(item => slot.lessonType && item.id === slot.lessonType.id)) {
            acc.push(slot.lessonType);
        }
        return acc;
    }, []).sort((a, b) => a.name.localeCompare(b.name))  : [], [visibleSlots]);

    const filteredSlots = useMemo(() => visibleSlots ? visibleSlots.filter(slot =>
        // Filter "My Bookings" selection
        (!filterBookedOnly || slot.isBooked)
        // Filter availability — move mode's target signal is canMoveTo, not
        // canBook (see SlotButtons for why): otherwise a limit-full learner's
        // Available filter would show nothing while picking a move destination.
        &&  (!filterAvailableOnly || (movingBooking ? slot.canMoveTo : slot.canBook) || slot.canCancel || slot.canCancelWait || slot.isBooked || slot.isWaitingBooked)
        // Filter Lesson Type selection
        &&  (!filterSlotType || slot.lessonType?.id === filterSlotType)
        // Filter Facility selection
        &&  (!filterFacility || slot.facilityId === filterFacility)
        // Filter Employee selection
        &&  (!filterEmployee || slot.employees?.find(e => e.id === filterEmployee))
    ).sort((a, b) => a.startTimeTs - b.startTimeTs) : [], [visibleSlots, filterBookedOnly, filterAvailableOnly, filterSlotType, filterFacility, filterEmployee, movingBooking]);

    const availableDates = useMemo(() => {
        const dates: AvailableDateOption[] = [];
        const startWeekDate = getSelectedWeekStartDate();
        for (let i = 0; i < 7; i++) {
            let isBooked: boolean = false;
            let isWaitingBooked: boolean = false;
            const workingTimestamp = startWeekDate.copy().addDays(i).toTs();

            if (slots) {
                Object.entries(slots).every(([slotId, slot]) => {
                    if (slot.isBooked && `${slot.dow}` === `${tsToDayOfWeekNum(workingTimestamp)}`
                    ) {
                        isBooked = true;
                        return false;
                    }
                    return true;
                });

                Object.entries(slots).every(([slotId, slot]) => {
                    if (slot.isWaitingBooked && `${slot.dow}` === `${tsToDayOfWeekNum(workingTimestamp)}`
                    ) {
                        isWaitingBooked = true;
                        return false;
                    }
                    return true;
                });
            }

            // The chip timestamp is a local midnight; the helper decodes the
            // API's holiday dates to local midnights of the same calendar dates.
            const isHoliday = isHolidayTs(holidays ?? [], workingTimestamp);

            // A frozen date's chip is inert so the calendar itself communicates
            // there is nothing to attend or book.
            const isFrozen = isTsWithinFreeze(frozenUntil, workingTimestamp);

            dates.push({
                // Browse rights are the server-emitted canManageBookings, never
                // isRecurring (that's booking-mode). A booked date stays tappable
                // regardless so a customer can always see their own bookings.
                active: !isFrozen && (isBooked || !!profilePackage?.canManageBookings),
                ts: workingTimestamp,
                date: tsToDate(workingTimestamp),
                isHoliday: isHoliday,
                isFrozen: isFrozen,
                isBooked: isBooked,
                isWaitingBooked: isWaitingBooked,
                isPast: workingTimestamp < selectedDate,
            });
        }

        return dates.sort((a, b) => (a.ts > b.ts ? 1 : -1));
    }, [
        selectedWeekIndex,
        selectedDate,
        slots,
        holidays,
        profilePackage,
        frozenUntil,
    ]);


    useEffect(() => {
        slotClearErrors();
        bookingsClearErrors();
    }, []);

    useEffect(() => {
        if (activeCustomer && profilePackageId && !profilePackage && !isFetchingPackages) {
            getProfilePackageById({ id: profilePackageId, customerId: activeCustomer.id });
        }
    }, [activeCustomer, profilePackageId, profilePackage, getProfilePackageById, isFetchingPackages]);


    useEffect(() => {
        if (profilePackageId && profilePackage) {
            if (lastProfilePackageId.current !== profilePackageId) {
                setSelectedWeekIndex(0);
                lastProfilePackageId.current = profilePackageId;
            }
        }
    }, [profilePackageId, profilePackage])

    useEffect(() => {
        if (!profilePackageId || selectedWeekIndex <= -99) return;

        setSelectedDraw({
            profilePackageId,
            startDate: dateToServerDateTs(getSelectedWeekStartDate().date),
        });
    }, [profilePackageId, selectedWeekIndex]);

    useEffect(() => {
        if(selectedDate && selectedDate > 0 && selectedWeekIndex > -99){
            setSelectedDateIsToday((new Ages(selectedDate).equalsDay(new Ages())))
        }
    }, [selectedDate]);

    useEffect(() => {
        if (profilePackage && selectedWeekIndex > -99) {
            fetchSlots(false, !isInitialLoadComplete, !isInitialLoadComplete);
        }
    }, [selectedWeekIndex, profilePackage?.id]);

    useEffect(() => {
        slotsErrorMessage && toastStore.showError({message: slotsErrorMessage}).then(() => {
            slotClearErrors();
        });
    }, [slotsErrorMessage])

    useEffect(() => {
        bookingsErrorMessage && toastStore.showError({message: bookingsErrorMessage}).then(() => {
            bookingsClearErrors();
        });
    }, [bookingsErrorMessage])

    function getSelectedWeekStartDate() {
        // Local clock only: startOfWeek handles Sunday itself (F17).
        return new Ages().startOfWeek().startOfDay().addWeeks(selectedWeekIndex); // Add the selected week index
    }

    /**
     * Determine and return the best day of the week for initial selection
     * after a new week is loaded
     * @param startWeekDate
     * @param slots
     */
    const getInitialSelectedDate = (startWeekDate: Ages, slots: Slot[]) => {
        const todayDate = (new Ages()).startOfDay();
        const endWeekDate = startWeekDate.copy().endOfWeek();
        const todayInSelectedWeek = startWeekDate.lte(todayDate) && endWeekDate.gte(todayDate);
        let selectedDate = todayInSelectedWeek ? todayDate : startWeekDate;
        const selectedDow = dateToDayOfWeekNum(selectedDate.date);
        const bookedDows = Object.entries(slots).filter(([id, slot]) => slot.isBooked).map(([id, slot]) => slot.dow)
            .filter((d, i, lds) => lds.indexOf(d) === i);
        const waitingBookedDows = Object.entries(slots).filter(([id, slot]) => slot.isWaitingBooked).map(([id, slot]) => slot.dow)
            .filter((d, i, lds) => lds.indexOf(d) === i); // Unique
        // If today is not selected, select the earliest
        // Day of week, where there is a booking
        if (! todayInSelectedWeek) {
            if (bookedDows.some(bookedDow => (bookedDow > selectedDow))) {
                let iProtect = 0;
                const earliestBookedDow = Math.min(...bookedDows.filter(bookingDow => (bookingDow >= selectedDow)));
                while (iProtect < 7 && (dateToDayOfWeekNum(selectedDate.date) < earliestBookedDow)) {
                    selectedDate = selectedDate.copy().addDays(1);
                    iProtect++;
                }
            }
            else if (waitingBookedDows.some(bookedDow => (bookedDow > selectedDow))) {
                let iProtect = 0;
                const earliestBookedDow = Math.min(...waitingBookedDows.filter(bookingDow => (bookingDow >= selectedDow)));
                while (iProtect < 7 && (dateToDayOfWeekNum(selectedDate.date) < earliestBookedDow)) {
                    selectedDate = selectedDate.copy().addDays(1);
                    iProtect++;
                }
            }
        }

        return selectedDate;
    }

    /**
     * Fetch and load the slots available for the profile package
     * Optionally fetch fresh booking and waiting booking records after the slots are loaded
     * @param clearDraw
     * @param fetchBookings
     * @param fetchWaitingBookings
     */
    const fetchSlots = (clearDraw: boolean = false, fetchBookings: boolean = false, fetchWaitingBookings: boolean = false) => {
        if (profilePackage){
            const startWeekDate = getSelectedWeekStartDate();
            // The API resolves this in the school's timezone; draw keys must use
            // the same value as the request so cached draws stay addressable.
            const startWeekTs = dateToServerDateTs(startWeekDate.date);
            const shouldSetDraw = clearDraw || !isInitialLoadComplete;

            const _afterFetchSlots = (slots: Slot[] = []) => {
                const action = shouldSetDraw ? setSelectedDraw({profilePackageId: profilePackage.id, startDate: startWeekTs}) : Promise.resolve();
                action.then(() => {
                    setSelectedDate(getInitialSelectedDate(startWeekDate, slots).toTs());
                });
            }
            const _fetchSlots = () => {
                Promise.all([
                    fetchBookings && bookingsStore.getAll({learnerId: profilePackage.learnerId}),
                    !!profilePackage.useWaiting && fetchWaitingBookings && waitingBookingsStore.getAll({learnerId: profilePackage.learnerId}),
                    getAllSlotsWeekly({profilePackageId: profilePackage.id, startDate: startWeekTs}),
                ])
                    .then(([bookingsResponse, waitingBookingsResponse, slotsResponse]) => {
                        if (slotsResponse?.results?.slots) {
                            // const {slots} = slotsResponse.results;
                            // If the selected date is in the selected period, then leave it
                            if (selectedDate < startWeekDate.toTs() || selectedDate > startWeekDate.copy().endOfWeek().toTs()) {
                                _afterFetchSlots(slotsResponse.results.slots);
                            }
                            else {
                            }
                        }
                    })
                    .catch(() => { /** handled by state and toasts */
                    })
                    .finally(() => {
                        setIsInitialLoadComplete(true);
                    });
            }

            // If we need to refresh the bookings or waiting bookings, then the
            // draws are no longer valid, and we need fresh slots data
            if (clearDraw || fetchBookings || fetchWaitingBookings){
                clearProfilePackageDraws({profilePackageId: profilePackage.id}).finally(() => {
                    _fetchSlots()
                });
            }
            else {
                // If we have gotten the data before and there have been no changes,
                // Then we can just use the same data by setting the selectedDraw Key
                const drawKey = `${profilePackage.id}-${startWeekTs}`;
                if(existingDrawKeys.includes(drawKey)){
                    _afterFetchSlots();
                }
                else {
                    _fetchSlots()
                }

            }
        }
    }


    /**
     * Change the selected week */
    const handlePreviousWeek = () => {
        if (selectedWeekIndex > -weeksAvailablePast) {
            setSelectedWeekIndex((state) => state - 1)
        }
    }

    /**
     * Change the selected week */
    const handleNextWeek = () => {
        if (selectedWeekIndex < weeksAvailableFuture) {
            setSelectedWeekIndex((state) => state + 1)
        }
    }

    /**
     * Generic handling of booking changes. Slots must be reloaded to get their status
     * after a change has been made
     */
    const _handleBookingAction = useCallback((callback: Promise<any>, fetchBookings: boolean = true) => {
        callback.catch(() => { /** Handled by state and toast */ })
            .finally(() => {
                setIsSlotModalOpen(false);
                fetchSlots(true);
            })
    }, [fetchSlots, toastStore])

    /**
     * Generic handling of booking changes. Slots must be reloaded to get their status
     * after a change has been made
     */
    const _handleWaitingBookingAction = useCallback((callback: Promise<any>) => {
        callback.catch(() => { /** Handled by state and toast */ })
            .finally(() => {
                setIsSlotModalOpen(false);
                fetchSlots(false, false, true);
            })
    }, [fetchSlots, toastStore])

    /**
     * Run `next` with the programme the customer picks when the API says this
     * slot needs a choice for the learner, or straight away (no id) when it
     * does not. Shared by the book and waiting-list flows: a waiting entry
     * carries the same pick so promotion lands on the programme the customer
     * meant, not a derived one.
     */
    const presentProgrammePickerThen = useCallback((targetSlot: Slot | undefined, description: string, message: string, next: (programmeId?: number) => void) => {
        const programmeChoice = targetSlot?.programmeChoice;
        if (!programmeChoice?.required) {
            next();
            return;
        }
        presentConfirmAlert({
            header: "Programme",
            subHeader: description,
            message: message,
            // The first option is pre-selected: the pick is mandatory here,
            // so there is no "none" and Continue always carries an id.
            inputs: programmeChoice.options.map((option, index) => ({
                type: 'radio' as const,
                label: option.name,
                value: option.id,
                checked: index === 0,
            })),
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Continue',
                    role: 'confirm',
                },
            ],
            // Presenting the confirm from Continue's handler races the picker's own
            // dismiss and the confirm lands overlay-hidden: the customer sees nothing
            // and nothing is posted. Chain it off the dismissal instead.
            onDidDismiss: (event: CustomEvent<{role?: string; data?: {values?: number}}>) => {
                if (event.detail.role === 'confirm' && event.detail.data?.values !== undefined) {
                    next(event.detail.data.values);
                }
            },
        })
    }, [presentConfirmAlert]);

    /**
     * Make a new booking, or — while a move is in progress — confirm the move
     * to this slot instead. Same button/handler for both: SlotButtons swaps the
     * label to "Move Here" once movingBooking is set.
     * @param slotId
     */
    const handleBookClicked = useCallback((slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        if (isSelectedDateFrozen) {
            toastStore.showError({message: `This profile is frozen until ${frozenUntil} and can't be booked for this date.`});
            return;
        }

        const targetSlot = filteredSlots.find((slot) => slot.id === slotId);

        if (movingBooking) {
            presentConfirmAlert({
                header: "Move Booking",
                subHeader: description,
                message: `Move ${movingBooking.description} to ${description}?`,
                buttons: [
                    {
                        text: 'Nope',
                        role: 'cancel',
                        handler: () => {
                        },
                    },
                    {
                        text: 'Yes',
                        role: 'confirm',
                        handler: () => {
                            if (profilePackage && targetSlot) {
                                // Only clear move mode on success — a failed move
                                // (e.g. the target filled up) leaves the user free
                                // to pick a different destination for the same booking.
                                _handleBookingAction(
                                    bookingMove({
                                        bookingId: movingBooking.bookingId,
                                        slotId: slotId,
                                        startedTimestamp: targetSlot.lessonDateTs,
                                    }).then(() => setMovingBooking(null))
                                );
                            } else {
                                toastStore.showError({message: "Failed to move booking. No package or slot selected."});
                            }
                        },
                    },
                ],
            })
            return;
        }

        const presentNewBookingConfirm = (programmeId?: number) => {
            presentConfirmAlert({
                header: "New Booking",
                subHeader: description,
                // A recurring booking is a standing weekly reservation, not a
                // single date — say so up front rather than implying a one-off.
                message: profilePackage?.isRecurring
                    ? `Book ${description} every week from ${dateToFormat(tsToDate(selectedDate), 'dd mmm yyyy')}?`
                    : "Are you sure you want to make this booking?",
                buttons: [
                    {
                        text: 'Nope',
                        role: 'cancel',
                        handler: () => {
                        },
                    },
                    {
                        text: 'Yes',
                        role: 'confirm',
                        handler: () => {
                            if (profilePackage) {
                                // The API start-of-day's this in the school's timezone —
                                // send a timestamp that resolves to the selected calendar date.
                                _handleBookingAction(bookingCreate({profilePackageId: profilePackage.id, slotId: slotId, startedTimestamp: dateToServerDateTs(tsToDate(selectedDate)), programmeId: programmeId}))
                            } else {
                                toastStore.showError({message: "Failed to create booking. No package selected."});
                            }
                        },
                    },
                ],
            })
        };

        presentProgrammePickerThen(targetSlot, description, "Which programme is this booking for?", presentNewBookingConfirm);

    }, [bookingCreate, bookingMove, profilePackage, selectedDate, toastStore, isSelectedDateFrozen, frozenUntil, movingBooking, filteredSlots, presentProgrammePickerThen]);

    /**
     * Enter move mode for a booked recurring occurrence: the next slot picked
     * via handleBookClicked becomes the move destination.
     */
    const handleMoveClicked = useCallback((bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        setMovingBooking({bookingId, description});
    }, []);

    /**
     * Leave move mode without moving anything.
     */
    const handleCancelMoveClicked = useCallback(() => {
        setMovingBooking(null);
    }, []);

    /**
     * Register on waiting list
     * @param slotId
     */
    const handleWaitClicked = useCallback((slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        if (isSelectedDateFrozen) {
            toastStore.showError({message: `This profile is frozen until ${frozenUntil} and can't join a waiting list for this date.`});
            return;
        }
        const targetSlot = filteredSlots.find((slot) => slot.id === slotId);

        const presentWaitingConfirm = (programmeId?: number) => {
            presentConfirmAlert({
                header: "Queue on Waiting list",
                subHeader: description,
                message: "Are you sure you want to add yourself to this waiting list?",
                buttons: [
                    {
                        text: 'Nope',
                        role: 'cancel',
                        handler: () => {
                        },
                    },
                    {
                        text: 'Yes',
                        role: 'confirm',
                        handler: () => {
                            if (profilePackage) {
                                // Same convention as bookingCreate: a timestamp the API
                                // resolves to the selected calendar date.
                                _handleWaitingBookingAction(waitingBookingCreate({profilePackageId: profilePackage.id, slotId: slotId, startedTimestamp: dateToServerDateTs(tsToDate(selectedDate)), programmeId: programmeId}))
                            } else {
                                toastStore.showError({message: "Failed to add to waiting list. No package selected."});
                            }
                        },
                    },
                ],
            })
        };

        presentProgrammePickerThen(targetSlot, description, "Which programme is this waiting-list entry for?", presentWaitingConfirm);

    }, [waitingBookingCreate, profilePackage, selectedDate, toastStore, isSelectedDateFrozen, frozenUntil, filteredSlots, presentProgrammePickerThen]);

    /**
     * Delete an existing booking
     * @param bookingId
     */
    const handleCancelWaitClicked = useCallback((waitingBookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        presentConfirmAlert({
            header: `Cancel waiting`,
            subHeader: description,
            message: `Are you sure you want to remove yourself from the waiting list?`,
            buttons: [
                {
                    text: 'Nope',
                    role: 'cancel',
                    handler: () => {
                    },
                },
                {
                    text: 'Yes',
                    role: 'confirm',
                    handler: () => {
                        if (profilePackage) {
                            _handleWaitingBookingAction(waitingBookingDelete({waitingBookingId: waitingBookingId}))
                        } else {
                            toastStore.showError({message: "Failed to delete waiting booking. No package selected."});
                        }
                    },
                },
            ],
        })
    }, [waitingBookingDelete, profilePackage, toastStore])

    /**
     * Delete an existing booking
     * @param bookingId
     */
    const handleDeleteClicked = useCallback((bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        presentConfirmAlert({
            header: `Cancel Booking`,
            subHeader: description,
            message: `Are you sure you want to cancel this booking?`,
            buttons: [
                {
                    text: 'Nope',
                    role: 'cancel',
                    handler: () => {
                    },
                },
                {
                    text: 'Yes',
                    role: 'confirm',
                    handler: () => {
                        if (profilePackage) {
                            _handleBookingAction(bookingDelete({bookingId: bookingId}))
                        } else {
                            toastStore.showError({message: "Failed to delete booking. No package selected."});
                        }
                    },
                },
            ],
        })
    }, [bookingDelete, profilePackage, toastStore])

    /**
     * Cancel the attendance for a specific date for a booking
     * @param bookingId
     * @param lessonDateTs
     */
    const handleCancelClicked = useCallback((bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        presentConfirmAlert({
            header: `Attendance Cancellation`,
            subHeader: description,
            message: `Are you sure you want to submit a cancellation for this booking? If your spot is taken, you may not be able to undo this.`,

            buttons: [
                {
                    text: 'Nope',
                    role: 'cancel',
                    handler: () => {
                    },
                },
                {
                    text: 'Yes',
                    role: 'confirm',
                    handler: () => {
                        if (profilePackage) {
                            _handleBookingAction(bookingCancel({bookingId, lessonDateTs}))
                        } else {
                            toastStore.showError({message: "Failed to cancel booking. No package selected."});
                        }
                    },
                },
            ],
        })

    }, [bookingCancel, profilePackage, toastStore]);

    /**
     * Undo a cancelled attendance for a specific date for a booking, provided
     * the occurrence hasn't been made up and a seat is still available — the
     * API is the capacity authority and may 400 with a friendly message even
     * though the button is showing (decision 8: the flag is capacity-blind).
     * @param bookingId
     * @param lessonDateTs
     */
    const handleUncancelClicked = useCallback((bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        event && event.stopPropagation();
        presentConfirmAlert({
            header: `Undo Cancellation`,
            subHeader: description,
            message: `Are you sure you want to undo this cancellation and rebook this date?`,

            buttons: [
                {
                    text: 'Nope',
                    role: 'cancel',
                    handler: () => {
                    },
                },
                {
                    text: 'Yes',
                    role: 'confirm',
                    handler: () => {
                        if (profilePackage) {
                            _handleBookingAction(bookingUncancel({bookingId, lessonDateTs}))
                        } else {
                            toastStore.showError({message: "Failed to undo cancellation. No package selected."});
                        }
                    },
                },
            ],
        })

    }, [bookingUncancel, profilePackage, toastStore]);

    const handleSlotClick = (slot: Slot) => {
        if (isFetching){ return;}
        setSelectedSlot(slot);
        setIsSlotModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedSlot(undefined);
        setIsSlotModalOpen(false);
    };

    const handleSlotFilterOpenClick = () => {
        setIsSlotFilterModalOpen(true);
    };

    const handleCloseSlotFilterModal = () => {
        setIsSlotFilterModalOpen(false);
    };



    return <SlotsPage
        company={company}
        learner={profile}
        profilePackage={profilePackage}
        frozenUntil={frozenUntil}
        isSelectedDateFrozen={isSelectedDateFrozen}
        selectedHolidayLabel={selectedHolidayLabel}
        availableDates={availableDates}
        slots={filteredSlots}
        bookings={bookings}
        facilities={facilities}
        slotTypes={slotTypes}
        employees={employees}

        onBookClicked={handleBookClicked}
        onWaitClicked={handleWaitClicked}
        onCancelWaitClicked={handleCancelWaitClicked}
        onDeleteClicked={handleDeleteClicked}
        onCancelClicked={handleCancelClicked}
        onUncancelClicked={handleUncancelClicked}
        onMoveClicked={handleMoveClicked}
        onCancelMoveClicked={handleCancelMoveClicked}
        movingBooking={movingBooking}
        isInitialLoading={isInitialLoading}
        isRefreshing={isRefreshing}
        isChangingBooking={isChangingBooking}
        isChangingWeek={isChangingWeek}
        actionsDisabled={actionsDisabled}

        setSelectedDate={setSelectedDate}
        selectedDate={selectedDate}
        selectedDateIsToday={selectedDateIsToday}
        selectedWeekIndex={selectedWeekIndex}
        weeksAvailableFuture={weeksAvailableFuture}
        weeksAvailablePast={weeksAvailablePast}
        onPreviousWeekClicked={handlePreviousWeek}
        onNextWeekClicked={handleNextWeek}

        isSlotFilterModalOpen={isSlotFilterModalOpen}
        onSlotFilterOpenClick={handleSlotFilterOpenClick}
        onCloseSlotFilterModal={handleCloseSlotFilterModal}

        filterAvailableOnly={filterAvailableOnly}
        setFilterAvailableOnly={setFilterAvailableOnly}
        filterBookedOnly={filterBookedOnly}
        setFilterBookedOnly={setFilterBookedOnly}
        filterSlotType={filterSlotType}
        setFilterSlotType={setFilterSlotType}
        filterFacility={filterFacility}
        setFilterFacility={setFilterFacility}
        filterEmployee={filterEmployee}
        setFilterEmployee={setFilterEmployee}

        selectedSlot={selectedSlot}
        isSlotModalOpen={isSlotModalOpen}
        onSlotClick={handleSlotClick}
        onCloseModal={handleCloseModal}
    />;
};
